import { Tooltip } from "@chakra-ui/react";
import { useState } from "react";
import styled from "styled-components";
import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { getActorInvitationReasonTranslationKey } from "src/contexts/documentationArea/domain/ActorInvitationReasonTranslations";
import { ActorInvitationReason } from "src/contexts/documentationArea/domain/ActorInvitation";
import { ActorInvitationModal } from "./ActorInvitationModal";

import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { colors } from "@permisso/permisso-components";

const INVITE_BUTTON_TEXT = "documentation_area.actor_invitation.button";

const INVITE_HIDDEN_REASONS = new Set<ActorInvitationReason>([
  ActorInvitationReason.DISABLED_BY_POLICY,
  ActorInvitationReason.UNSUPPORTED_ACTOR_TYPE_FOR_INVITATION,
]);

type ActorInvitationActionProps = {
  iconOnly?: boolean;
};

export const ActorInvitationAction = ({ iconOnly = false }: ActorInvitationActionProps) => {
  const { t } = useTranslations();
  const { actorInvitation, isReadOnly } = useDocumentationArea();
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (isReadOnly) {
    return null;
  }

  // Backward-compatible fallback while backend rollout is in progress.
  // If actorInvitation is missing, keep invite enabled by default.
  const effectiveActorInvitation = actorInvitation ?? { enabled: true, reason: null };
  const shouldHideInviteAction =
    effectiveActorInvitation.reason !== null &&
    INVITE_HIDDEN_REASONS.has(effectiveActorInvitation.reason);

  if (shouldHideInviteAction) {
    return null;
  }

  const isEnabled = effectiveActorInvitation.enabled;
  const reasonKey = getActorInvitationReasonTranslationKey(effectiveActorInvitation.reason);

  return (
    <>
      <Tooltip hasArrow label={reasonKey ? t(reasonKey) : ""} isDisabled={isEnabled || !reasonKey}>
        <InviteButton
          onClick={() => {
            if (!isEnabled) {
              return;
            }
            setIsModalOpen(true);
          }}
          disabled={!isEnabled}
          $iconOnly={iconOnly}
        >
          <InviteIcon>
            <Icon
              size={ICON_SIZES.s16}
              color={isEnabled ? colors.grey800 : colors.grey500}
              path={ICON_PATHS.PLUS_LARGE}
            />
          </InviteIcon>
          {iconOnly ? null : t(INVITE_BUTTON_TEXT)}
        </InviteButton>
      </Tooltip>
      <ActorInvitationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

const InviteButton = styled.button<{ disabled: boolean; $iconOnly: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ $iconOnly }) => ($iconOnly ? "0" : "0.5rem")};
  min-width: ${({ $iconOnly }) => ($iconOnly ? "2.5rem" : "6.875rem")};
  min-height: 2.5rem;
  padding: 0.5rem;
  background-color: ${colors.grey300};
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0;
  color: ${({ disabled }) => (disabled ? colors.grey500 : colors.grey800)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s ease;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover:not(:disabled) {
    background-color: ${colors.grey400};
  }
`;

const InviteIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 1.5rem;
  width: 1.5rem;
  height: 1.25rem;
`;
